import { HttpErrorResponse } from "@angular/common/http";
import { computed, inject } from "@angular/core";
import { tapResponse } from "@ngrx/operators";
import { patchState, signalStore, withComputed, withMethods, withState } from "@ngrx/signals";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { EMPTY, filter, pipe, switchMap, tap } from "rxjs";

import { setPropError, setPropInit, setPropLoaded, setPropLoading, withReqState } from "@application/data-access";
import { ErrorStore } from "@application/errors/data-access";

import {
  BiometricReader,
  EnrolledTemplate,
  FingerPosition,
  initialProfileBiometricsStore,
  IProfileBiometricsStore,
  TemplateRaw,
} from "./entities/profile-biometrics.model";
import { ProfileBiometricsService } from "./infra/profile-biometrics.service";

const ENROLL_INVARIANT_ERROR = "Cannot enroll: pendingTemplates must have exactly 3 entries";

export const ProfileBiometricsStore = signalStore(
  { providedIn: "root" },
  withState<IProfileBiometricsStore>(initialProfileBiometricsStore),
  withReqState(
    "loadReaders",
    "capture",
    "validateLast",
    "compareWith",
    "enroll",
    "loadEnrolledTemplates",
    "removeTemplate",
  ),
  withComputed((store) => ({
    canEnroll: computed<boolean>(() =>
      store.pendingTemplates().length === 3 && !store.enrollLoading(),
    ),
  })),
  withMethods((
    store,
    service = inject(ProfileBiometricsService),
    errorStore = inject(ErrorStore),
  ) => {
    const loadReaders = rxMethod<string>(pipe(
      tap(() => patchState(store, setPropLoading("loadReaders"))),
      switchMap((edgeId) =>
        service.getBiometricReaders(edgeId).pipe(
          tapResponse({
            next: (readers) => patchState(store, { readers }, setPropLoaded("loadReaders")),
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, setPropError("loadReaders", error));
            },
          }),
        ),
      ),
    ));

    const selectReader = (reader: BiometricReader | null): void => {
      patchState(store, { selectedReader: reader });
    };

    const capture = rxMethod<string>(pipe(
      tap(() => patchState(store, setPropLoading("capture"))),
      switchMap((edgeId) =>
        service.captureBiometric(edgeId).pipe(
          tapResponse({
            next: (template) => patchState(
              store,
              {
                lastCapture: template,
                lastCaptureIsValid: null,
                lastCompareMatched: null,
              },
              setPropLoaded("capture"),
            ),
            error: (error: HttpErrorResponse) => {
              // A 409 Conflict from capture-biometric carries a plain-text
              // reason (e.g. finger already in use / reader busy). Surface that
              // text inline instead of the generic global error toast.
              const conflictText =
                error.status === 409 && typeof error.error === "string"
                  ? error.error.trim()
                  : "";
              if (conflictText) {
                patchState(
                  store,
                  { lastCapture: null, lastCaptureIsValid: null },
                  setPropError("capture", conflictText),
                );
                return;
              }
              errorStore.setHttpError(error);
              patchState(
                store,
                { lastCapture: null, lastCaptureIsValid: null },
                setPropError("capture", error),
              );
            },
          }),
        ),
      ),
    ));

    const validateLast = rxMethod<void>(pipe(
      filter(() => store.lastCapture() !== null),
      tap(() => patchState(store, setPropLoading("validateLast"))),
      switchMap(() =>
        service.isValidTemplate(store.lastCapture() as TemplateRaw).pipe(
          tapResponse({
            next: (isValid) => patchState(
              store,
              { lastCaptureIsValid: isValid },
              setPropLoaded("validateLast"),
            ),
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(
                store,
                { lastCaptureIsValid: null },
                setPropError("validateLast", error),
              );
            },
          }),
        ),
      ),
    ));

    const compareWith = rxMethod<TemplateRaw>(pipe(
      filter(() => store.lastCapture() !== null),
      tap(() => patchState(store, setPropLoading("compareWith"))),
      switchMap((otherTemplateRaw) =>
        service.verifyMatched(store.lastCapture() as TemplateRaw, otherTemplateRaw).pipe(
          tapResponse({
            next: (matched) => patchState(
              store,
              { lastCompareMatched: matched },
              setPropLoaded("compareWith"),
            ),
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(
                store,
                { lastCompareMatched: null },
                setPropError("compareWith", error),
              );
            },
          }),
        ),
      ),
    ));

    const acceptLastCapture = (): void => {
      const template = store.lastCapture();
      const isValid = store.lastCaptureIsValid();
      const buffer = store.pendingTemplates();
      const matched = store.lastCompareMatched();

      if (template === null || isValid !== true) return;
      if (buffer.length > 0 && matched !== true) return;

      patchState(store, {
        pendingTemplates: [...buffer, template],
        lastCapture: null,
        lastCaptureIsValid: null,
        lastCompareMatched: null,
      });
    };

    const loadEnrolledTemplates = rxMethod<string>(pipe(
      tap(() => patchState(store, setPropLoading("loadEnrolledTemplates"))),
      switchMap((personId) =>
        service.listTemplatesForPerson(personId).pipe(
          tapResponse({
            next: (enrolledTemplates) => patchState(
              store,
              { enrolledTemplates },
              setPropLoaded("loadEnrolledTemplates"),
            ),
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, setPropError("loadEnrolledTemplates", error));
            },
          }),
        ),
      ),
    ));

    const enroll = rxMethod<{ personId: string; finger: FingerPosition }>(pipe(
      switchMap(({ personId, finger }) => {
        if (!store.canEnroll()) {
          patchState(store, setPropError("enroll", new Error(ENROLL_INVARIANT_ERROR)));
          return EMPTY;
        }

        patchState(store, setPropLoading("enroll"));

        return service.addTemplate(personId, store.pendingTemplates(), finger).pipe(
          tapResponse({
            next: (created) => {
              const remaining = store.enrolledTemplates().filter((t) => t.finger !== finger);
              const inserted: EnrolledTemplate | null = created
                ?? { templateId: "", finger };

              patchState(
                store,
                {
                  pendingTemplates: [],
                  lastCapture: null,
                  lastCaptureIsValid: null,
                  lastCompareMatched: null,
                  enrolledTemplates: [...remaining, inserted],
                },
                setPropLoaded("enroll"),
              );

              if (!created) {
                loadEnrolledTemplates(personId);
              }
            },
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, setPropError("enroll", error));
            },
          }),
        );
      }),
    ));

    const removeTemplate = rxMethod<string>(pipe(
      tap(() => patchState(store, setPropLoading("removeTemplate"))),
      switchMap((templateId) =>
        service.removeTemplate(templateId).pipe(
          tapResponse({
            next: () => patchState(
              store,
              {
                enrolledTemplates: store.enrolledTemplates().filter((t) => t.templateId !== templateId),
              },
              setPropLoaded("removeTemplate"),
            ),
            error: (error: HttpErrorResponse) => {
              errorStore.setHttpError(error);
              patchState(store, setPropError("removeTemplate", error));
            },
          }),
        ),
      ),
    ));

    const resetCapture = (): void => {
      patchState(store, {
        lastCapture: null,
        lastCaptureIsValid: null,
        lastCompareMatched: null,
        pendingTemplates: [],
      }, setPropInit("capture", "validateLast", "compareWith"));
    };

    const clearFingerprintTemplateError = (): void => {
      patchState(store, setPropInit("enroll", "removeTemplate"));
    };

    const forPerson = (personId: string): void => {
      patchState(store, {
        personId,
        lastCapture: null,
        lastCaptureIsValid: null,
        lastCompareMatched: null,
        pendingTemplates: [],
        enrolledTemplates: [],
      }, setPropInit("capture", "validateLast", "compareWith", "enroll"));

      loadEnrolledTemplates(personId);
    };

    return {
      loadReaders,
      selectReader,
      capture,
      validateLast,
      compareWith,
      acceptLastCapture,
      loadEnrolledTemplates,
      enroll,
      removeTemplate,
      resetCapture,
      clearFingerprintTemplateError,
      forPerson,
    };
  }),
);
